import { TFunction } from "i18next";
import { formatDate } from "./DateUtil";

// A stamp younger than this reads "Stock updated just now" - the verify
// answer at the confirmation step lands seconds before it is shown.
export const STOCK_JUST_NOW_MS = 60_000;

const ZONELESS_STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * Milliseconds of an ISO stamp. The API serializes an EF-read DateTime
 * WITHOUT a zone suffix ("2026-09-07T10:00:00"), which Date.parse would read
 * as local time - shifting both the shown time and the 24 h staleness
 * boundary by the browser's UTC offset - so a zoneless stamp is taken as UTC
 * (same rule as the import-status / system-health screens). NaN for anything
 * unparsable.
 */
export function parseUtcStamp(iso: string): number {
  const text = iso.trim();
  return Date.parse(ZONELESS_STAMP.test(text) ? `${text}Z` : text);
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * The secondary line under a storage count (flags checkoutStorageVerifyEnabled
 * / storageFreshnessEnabled): "Stock not verified yet" for a missing/unset
 * stamp (a legacy StorageCounts row that was never written from NAV - order
 * 20007070's 194), "Stock updated just now" inside STOCK_JUST_NOW_MS,
 * otherwise "Stock updated {date} {time}" in the app's short date format and
 * the browser's local time.
 */
export function storageUpdatedLabel(
  iso: string | null | undefined,
  t: TFunction,
  nowMs: number = Date.now(),
): string {
  if (!iso) return t("compLogoDesign.stockNotVerified");
  const ms = parseUtcStamp(iso);
  if (Number.isNaN(ms) || ms <= 0) return t("compLogoDesign.stockNotVerified");
  if (nowMs - ms < STOCK_JUST_NOW_MS) {
    return t("compLogoDesign.stockUpdatedJustNow");
  }
  const date = new Date(ms);
  return t("compLogoDesign.stockUpdated", {
    date: formatDate(date),
    time: formatTime(date),
    interpolation: { escapeValue: false },
  });
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * The compact form of the same line (Nichlas 8/9: "the text takes too much
 * room"): "not verified" for a missing/unset stamp, "just now" inside
 * STOCK_JUST_NOW_MS (a stamp in the future included - clock skew), then
 * "12 min ago" / "3 h ago" / "2 d ago", floored. The full date/time stays
 * in storageUpdatedLabel for the tooltip. No date library in the app, and
 * the admin API-keys formatter has its own namespace and 30-day rule, so a
 * small helper of its own.
 */
export function storageAgeLabel(
  iso: string | null | undefined,
  t: TFunction,
  nowMs: number = Date.now(),
): string {
  if (!iso) return t("compLogoDesign.stockAgeNotVerified");
  const ms = parseUtcStamp(iso);
  if (Number.isNaN(ms) || ms <= 0) return t("compLogoDesign.stockAgeNotVerified");
  const age = nowMs - ms;
  if (age < STOCK_JUST_NOW_MS) return t("compLogoDesign.stockAgeJustNow");
  if (age < HOUR_MS) {
    return t("compLogoDesign.stockAgeMinutes", { n: Math.floor(age / MINUTE_MS) });
  }
  if (age < DAY_MS) {
    return t("compLogoDesign.stockAgeHours", { n: Math.floor(age / HOUR_MS) });
  }
  return t("compLogoDesign.stockAgeDays", { n: Math.floor(age / DAY_MS) });
}
